import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@mui/material';

import SessionForm from '@features/subpages/components/SessionForm';
import { useIsSignin } from '@shared/hooks';
import { forcedLogout, userInitiatedLogout } from '@shared/utils/authCleanup';
import { getSessionStatus, renewToken, setPreExpiryPopupOpen } from '@shared/utils/sessionUtils';
import { getUserEncrypted } from '@shared/utils/storageUtils';

const WARNING_THRESHOLD_SEC = 60;
const POLL_MS = 1000;
const POST_ACTION_QUIET_MS = 16000;
const SESSION_DIALOG_Z_INDEX = 10_000;

const computeRemainingSec = (): number | null => {
  const expiresAt = getSessionStatus()?.expiresAt;
  if (!expiresAt) return null;
  return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
};

const SessionExpirationWatcher: React.FC = () => {
  const isSignin = useIsSignin();
  const [showModal, setShowModal] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);
  const [isRenewing, setIsRenewing] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);
  const quietUntilRef = useRef(0);

  useEffect(() => {
    if (!isSignin) {
      setShowModal(false);
      setRenewError(null);
      return;
    }

    const tick = () => {
      if (Date.now() < quietUntilRef.current) return;

      const remaining = computeRemainingSec();
      if (remaining === null) {
        setShowModal(false);
        return;
      }
      if (remaining <= 0) {
        setShowModal(false);
        forcedLogout('expired');
        return;
      }
      if (remaining <= WARNING_THRESHOLD_SEC) {
        setShowModal(true);
        setRemainingSec(remaining);
      } else {
        setShowModal(false);
        setRenewError(null);
      }
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [isSignin]);

  // Suspend silent renewals while the warning is visible — the user must
  // explicitly choose Continuar/Salir or let the session expire.
  useEffect(() => {
    setPreExpiryPopupOpen(showModal);
    return () => setPreExpiryPopupOpen(false);
  }, [showModal]);

  const handleStay = async () => {
    if (isRenewing) return;
    setIsRenewing(true);
    setRenewError(null);
    try {
      const result = await renewToken();
      if (!result.success) {
        setRenewError(result.error ?? 'No se pudo renovar la sesión. Intenta de nuevo.');
        return;
      }

      const remainingAfterRenew = computeRemainingSec();
      if (remainingAfterRenew !== null && remainingAfterRenew <= WARNING_THRESHOLD_SEC) {
        setRenewError(
          'La sesión se renovó, pero el tiempo de expiración no se actualizó. Intenta de nuevo.'
        );
        setRemainingSec(remainingAfterRenew);
        return;
      }

      quietUntilRef.current = Date.now() + POST_ACTION_QUIET_MS;
      setShowModal(false);
      setRenewError(null);
    } finally {
      setIsRenewing(false);
    }
  };

  const handleLogout = () => {
    quietUntilRef.current = Date.now() + POST_ACTION_QUIET_MS;
    setShowModal(false);
    setRenewError(null);
    const profileId = getUserEncrypted()?.profile_id;
    void userInitiatedLogout(profileId);
  };

  return (
    <Dialog
      open={showModal}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // The session warning is intentional — only the action buttons can close it.
        if (reason === 'backdropClick') return;
      }}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: SESSION_DIALOG_Z_INDEX }}
      slotProps={{
        root: { sx: { zIndex: SESSION_DIALOG_Z_INDEX } },
      }}>
      <SessionForm
        handleLogOut={handleLogout}
        handleStayLoggedIn={handleStay}
        remainingTime={remainingSec}
        isRenewing={isRenewing}
        renewError={renewError}
      />
    </Dialog>
  );
};

export default SessionExpirationWatcher;
